<script setup>
import { computed, nextTick, onBeforeMount, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';

import bootbox from 'bootbox';

import DsAlert from 'JIX/components/ds/DsAlert.vue';
import DsOptionsMenu from 'JIX/components/ds/DsOptionsMenu.vue';
import SvgIcon from 'JIX/components/SvgIcon.vue';

import ApiConflictError from "JIX/errors/ApiConflictError.js";
import ApiValidationError from 'JIX/errors/ApiValidationError';
import RequestError from 'JIX/errors/RequestError';

import { __ } from 'JIX/gettext.js';
import { createJobagent, updateJobagent } from 'JIX/jobagent/api.js';
import { processParams } from 'JIX/jobsearch/utils';
import { isLoggedIn } from 'JIX/utils/login.js';

import 'icons/color-tip.svg';
import 'icons/complete.svg';

const store = useStore();

const jobagent_subscribed = ref(store.state.searchResponse.jobagent?.active);
const jobagent_switch_on = ref(jobagent_subscribed.value);
const jobagent_id = ref(store.state.searchResponse.jobagent?.id);
const jobagent_url = ref(store.state.searchResponse.jobagent?.url);
const jobagent_status_message = ref('');
const display_success_checkmark = ref(false);

const user_logged_in = ref(!!window.Stash.common.userid);
const email = ref(null);
const processing = ref(false);
const emailInput = ref(null);

const showForm = computed(() => jobagent_switch_on.value && !user_logged_in.value && !jobagent_subscribed.value);

function handleEnterMail() {
    newJobagent();
}

async function updateLoginStatus() {
    processing.value = true;
    const loginResult = await isLoggedIn('user');
    processing.value = false;
    user_logged_in.value = loginResult;
}

onBeforeMount(updateLoginStatus);

watch(jobagent_switch_on, async () => {
    await updateLoginStatus();

    if (user_logged_in.value){
        if (jobagent_switch_on.value) {
            if (jobagent_id.value){
                //Subscribe to existing jobagent
                saveJobagent();
            } else {
                //Subscribe to new jobagent
                newJobagent();
            }
        } else if (jobagent_id.value){
            //Unsubscribe from jobagent
            saveJobagent();
        }
    } else if (jobagent_switch_on.value && !jobagent_subscribed.value){
        //Focus on email input
        await nextTick();
        emailInput.value.focus();
    }
});

async function newJobagent(){
    if (showApiValidationErrorsIfPresent()){
        return;
    }
    processing.value = true;
    const jobagentData = {
        id: null,
        enabled: true,
        search_params: processParams(store.state.searchQuery),
        frequency: 'daily',
        email: email.value
    };
    await createJobagent(jobagentData).then((response) => {
        jobagent_subscribed.value = true;
        jobagent_id.value = response.jobagent.id;
        jobagent_url.value = response.jobagent.edit_url;
        analytics();
        refreshStatusMessage();
    }).catch((error) => {
        processing.value = false;
        handleJobagentApiError(error);
    });
    processing.value = false;
}

async function saveJobagent(){
    if (showApiValidationErrorsIfPresent()){
        return;
    }
    processing.value = true;
    const updatedJobagent = {
        id: jobagent_id.value,
        enabled: jobagent_switch_on.value,
    };
    await updateJobagent(updatedJobagent).then(() => {
        jobagent_subscribed.value = jobagent_switch_on.value;
        analytics();
        refreshStatusMessage();
    }).catch((error) => {
        processing.value = false;
        handleJobagentApiError(error);
    });
    processing.value = false;
}

function analytics() {
    const params = processParams(store.state.searchQuery);
    window.jixAnalytics('event', { category: 'Jobagent', action: 'add_jobagent', label: params.q });
}

function handleJobagentApiError(error){
    jobagent_status_message.value = __('Der opstod en fejl');
    if (error instanceof ApiConflictError && error.error === 'duplicate_jobagent') {
        showDuplicateJobagentError();
    } else if (error instanceof ApiValidationError){
        showInvalidJobagentInputError();
    } else if (error instanceof RequestError && error.status === 404){
        showJobagentNotFoundError();
    } else {
        throw error;
    }
}

function showApiValidationErrorsIfPresent(){
    if (store.state.apiValidationErrors && store.state.apiValidationErrors.length > 0){
        bootbox.dialog({
            title: __("Fejl"),
            message: __("Der er ugyldige værdier i dine filtre: ") + store.state.apiValidationErrors[0].error_message,
            size: "large",
            onEscape: true,
            onBackdrop: true,
            centerVertical: true,
            buttons: {
                ok: {
                    label: __("Ok"),
                    className: 'btn-primary',
                }
            },
        });
        return true;
    }
    return false;
}

function showErrorDialog(errorMessage){
    bootbox.dialog({
        title: __("Fejl"),
        message: errorMessage,
        size: "large",
        onEscape: true,
        onBackdrop: true,
        centerVertical: true,
        buttons: {
            reload: {
                label: __("Genindlæs"),
                className: 'btn btn-primary',
                callback: function() {
                    location.reload();
                }
            },
        },
    });
}

function showJobagentNotFoundError(){
    showErrorDialog(__("Jobagenten kunne ikke findes"));
}

function showDuplicateJobagentError(){
    showErrorDialog(__("Du har allerede en Jobagent med samme søgeparametre"));
}

function showInvalidJobagentInputError(){
    //Todo: Open the filters and show the user where the error is
    showErrorDialog(__("Filtrene indeholdt ugyldige værdier"));
}

const menuItems = computed(() => [
    {
        key: 'toggle_subscription',
        label: jobagent_switch_on.value ? __("Afmeld Jobagent") : __("Tilmeld Jobagent"),
        onclick() {
            jobagent_switch_on.value = !jobagent_switch_on.value;
        }
    },
    {
        key: 'edit',
        label: __("Ret Jobagent"),
        url: jobagent_url.value,
        hidden: jobagent_id.value === undefined
    },
    {
        key: 'overview',
        label: __("Se Jobagenter"),
        url: store.state.jobagentRootUrl
    },
].filter(item => !item.hidden));

function refreshStatusMessage(){
    display_success_checkmark.value = false;
    if (jobagent_switch_on.value && !user_logged_in.value && !jobagent_subscribed.value){
        jobagent_status_message.value = __('Anmodning afsendt');
    } else if (jobagent_switch_on.value){
        display_success_checkmark.value = true;
        jobagent_status_message.value = __('Jobagent tilmeldt');
    } else if (!jobagent_switch_on.value && jobagent_id.value){
        jobagent_status_message.value = __('Få nye job sendt på mail');
    } else {
        jobagent_status_message.value = '';
    }
}

onMounted(() => {
    refreshStatusMessage();
});
</script>

<template>
    <DsAlert type="info">
        <div class="d-flex justify-content-between align-items-center gap-4">
            <div>
                <div class="heading-tag jix-small-tip__heading-tag" v-if="!jobagent_status_message">
                    {{ __('Tip') }}
                    <SvgIcon
                        name="color-tip"
                        size="xs"
                        role="presentation"
                    />
                </div>
                <SvgIcon
                    class="mr-2 text-success"
                    v-if="display_success_checkmark"
                    name="complete"
                    size="xs"
                />
                <strong v-if="jobagent_status_message">{{ jobagent_status_message }}</strong>
                <template v-else>
                    <h5 class="jix-small-tip__heading d-none d-md-block">
                        {{ __('Vil du modtage denne slags jobannoncer på mail?') }}
                    </h5>
                    <h5 class="jix-small-tip__heading d-md-none">
                        {{ __('Få nye job sendt på mail') }}
                    </h5>
                </template>
            </div>
            <button
                type="button"
                class="btn btn-primary btn-sm"
                @click="jobagent_switch_on = true"
                v-if="!jobagent_subscribed"
                :hidden="showForm"
            >
                {{ jobagent_id ? __('Aktiver Jobagent') : __('Opret Jobagent') }}
            </button>
            <template v-else>
                <DsOptionsMenu
                    id="jobagent-menu"
                    :items="menuItems"
                    class="ml-3"
                    size="sm"
                    v-if="user_logged_in"
                />
            </template>
        </div>
        <form @submit.prevent="handleEnterMail" class="d-flex flex-column align-items-end mt-3" v-if="showForm">
            <div class="d-flex flex-column w-100">
                <label class="control-label">{{ __('E-mail') }}</label>
                <input
                    ref="emailInput"
                    v-model="email"
                    type="email"
                    required
                    autocomplete="email"
                    :placeholder="__('Indtast e-mail som vi skal sende jobannoncer til')"
                    class="form-control"
                >
            </div>
            <button type="submit" class="btn btn-primary btn-sm mt-3">
                {{ __('Opret Jobagent') }}
            </button>
        </form>
    </DsAlert>
</template>